import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import {
    SearchContainer,
    CatMenu,
    RestaurantIconContainer,
    ToolIcon,
} from "./style";
import Fuse from "fuse.js";
import InlineNotification from "../InlineNotification";
import CatCaption from "../CatCaption";
import ListItem from "../List/ListItem";
import Restaurant from "~/static/images/icons/restaurant.svg";
import ChevronLeft from "~/static/images/icons/chevron-left.svg";
import Remove from "~/static/images/icons/remove.svg";
import Search from "~/static/images/icons/search.svg";
import { easyTranslate } from "../../Helpers/util";
import translations from "../../Translations/translations_DE";
import { colors } from "../../theme/constants";
import SvgMask from "../SvgMask";

let delayTimeout = null;

const KonfiguratorSearchBar = (props) => {
    const [value, setValue] = useState("");
    const [focused, setFocused] = useState(false);
    const [searchInAttrs, setSearchInAttrs] = useState(undefined);
    const [searchInIntols, setSearchInIntols] = useState(undefined);

    const containerRef = useRef(null);

    const normalizeEntry = (entry) => {
        if (typeof entry === "string") return entry;
        else {
            return entry.props["data-name"];
        }
    };

    useEffect(() => {
        if (!props.onSearch) return;
        const searchCallback =
            !searchInAttrs && !searchInIntols
                ? () => {
                      const fuse = new Fuse(props.data, {
                          keys: props.searchKeys,
                      });
                      const filtered = fuse.search(normalizeEntry(value));
                      if (props.onSearch) props.onSearch(filtered);
                  }
                : () => {
                      const filtered = props.data
                          .filter((item) => {
                              if (searchInIntols) {
                                  return !item.intolerances.find(
                                      (x) => x === searchInIntols
                                  );
                              } else {
                                  return item.attributes.find(
                                      (x) => x === searchInAttrs
                                  );
                              }
                          })
                          .map((x) => ({ item: x, refIndex: 0 }));
                      if (props.onSearch) props.onSearch(filtered);
                  };
        if (props.delay) {
            if (delayTimeout) {
                clearTimeout(delayTimeout);
            }
            delayTimeout = setTimeout(searchCallback, props.delay);
        } else {
            searchCallback();
        }
    }, [value]);

    const onBlur = (e) => {
        const inputs = document.getElementsByTagName("input");
        if (inputs.length === 1) {
            inputs[0].blur();
        }
        if (props.onBlur) props.onBlur(e);
        setTimeout(() => setFocused(false), 10);
    };

    const onFocus = () => {
        setFocused(true);
        if (props.onFocus) props.onFocus(value);
    };

    const onClear = (e) => {
        if (props.onClear) props.onClear();
        setSearchInAttrs(undefined);
        e && e.stopPropagation();
        if (!value || normalizeEntry(value).length === 0) {
            // onExit(e);
            return;
        }
        setValue("");
        const inputs = document.getElementsByTagName("input");
        if (inputs.length === 1) {
            inputs[0].focus();
        }
    };

    const onExit = (e) => {
        onClear(e);
        onBlur(e);
    };

    const attributes = props.ingredients
        .reduce((prev, curr) => {
            const ingredientCats = curr.attributes;
            let newAttrs = [];
            for (const ic of ingredientCats) {
                if (!prev.find((x) => x === ic)) {
                    newAttrs = [...newAttrs, ic];
                }
            }
            return [...prev, ...newAttrs];
        }, [])
        .sort((a, b) => (a < b ? -1 : 1));

    const intolerances = props.ingredients
        .reduce((prev, curr) => {
            const ingredientIntol = curr.intolerances;
            let newIntol = [];
            for (const ic of ingredientIntol) {
                if (!prev.find((x) => x === ic)) {
                    newIntol = [...newIntol, ic];
                }
            }
            return [...prev, ...newIntol];
        }, [])
        .sort((a, b) => (a < b ? -1 : 1));

    const renderMenuEntry = (cat, isIntolerance) => {
        const ingredientsInside = props.ingredients?.filter((ing) => {
            if (isIntolerance) {
                return !ing.intolerances.find((x) => x === cat);
            } else {
                return ing.attributes.find((x) => x === cat);
            }
        });

        const translated = easyTranslate(
            cat,
            translations.ingredientCategories
        );

        if (isIntolerance) {
            translated.name = `Ohne ${translated.name}`;
        }

        return (
            <ListItem
                key={cat}
                icon={
                    <div>
                        <RestaurantIconContainer>
                            <SvgMask
                                icon={Restaurant}
                                size={19}
                                color={colors.LAYOUT.GRAY[1000]}
                            />
                        </RestaurantIconContainer>
                    </div>
                }
                onClick={(e) => {
                    e.stopPropagation();
                    if (isIntolerance) {
                        setSearchInAttrs(undefined);
                        setSearchInIntols(cat);
                    } else {
                        setSearchInIntols(undefined);
                        setSearchInAttrs(cat);
                    }
                    setValue(translated.name);
                }}
                details={translated.description}
                right={`${ingredientsInside?.length} Zutat${
                    ingredientsInside?.length !== 1 ? "en" : ""
                }`}
            >
                {translated.name}
            </ListItem>
        );
    };

    return (
        <SearchContainer
            value={normalizeEntry(value)}
            focused={focused}
            ref={containerRef}
        >
            <input
                style={{
                    flex: 1,
                }}
                type="text"
                placeholder={props.placeholder}
                value={normalizeEntry(value)}
                onBlur={onBlur}
                onFocus={onFocus}
                onChange={({ target }) => {
                    setSearchInAttrs(undefined);
                    setSearchInIntols(undefined);
                    setValue(target.value);
                }}
            />
            <ToolIcon focused={focused} style={{ left: 12 }}>
                {focused || normalizeEntry(value).length > 0 ? (
                    <SvgMask
                        icon={ChevronLeft}
                        color={colors.BLACK}
                        size={24}
                        maskSize="33%"
                        onClick={onExit}
                    />
                ) : (
                    <SvgMask
                        style={{ pointerEvents: "none", userSelect: "none" }}
                        icon={Search}
                        color={colors.BRAND.SECONDARY[500]}
                        size={24}
                        maskSize="85%"
                        onClick={onExit}
                    />
                )}
            </ToolIcon>
            <ToolIcon
                focused={focused}
                style={{ right: 12, opacity: focused ? 1 : 0 }}
                onClick={onClear}
            >
                <SvgMask
                    icon={Remove}
                    color={colors.LAYOUT.GRAY[1000]}
                    size={24}
                    maskSize="85%"
                />
            </ToolIcon>
            <CatMenu
                className={
                    focused && normalizeEntry(value).length === 0
                        ? "visible"
                        : "invisible"
                }
                width={
                    containerRef && containerRef.current
                        ? (containerRef.current || { clientWidth: 0 })
                              .clientWidth
                        : 0
                }
            >
                <InlineNotification
                    big
                    type="info"
                    text="Du kannst nach Kategorien wie Käse, Dressings, Fleisch etc. und einzelnen Zutaten suchen."
                />
                <div style={{ backgroundColor: "#ffffff" }}>
                    <CatCaption amount={attributes ? attributes.length : 0}>
                        Zutaten filtern
                    </CatCaption>
                    <div>
                        {attributes?.map((entry) =>
                            renderMenuEntry(entry, false)
                        )}
                    </div>
                    <CatCaption amount={intolerances ? intolerances.length : 0}>
                        Nach Intoleranzen filtern
                    </CatCaption>
                    <div>
                        {intolerances?.map((entry) =>
                            renderMenuEntry(entry, true)
                        )}
                    </div>
                </div>
            </CatMenu>
        </SearchContainer>
    );
};

KonfiguratorSearchBar.defaultProps = {
    data: [],
    searchKeys: [],
    autoCompleteKey: "",
    ingredients: [],
};

KonfiguratorSearchBar.propTypes = {
    data: PropTypes.array,
    onSearchStart: PropTypes.func,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    onSearch: PropTypes.func,
    onClear: PropTypes.func,
    placeholder: PropTypes.string,
    delay: PropTypes.number,
    searchKeys: PropTypes.array,
    autoCompleteKey: PropTypes.string,
    ingredients: PropTypes.array,
};

export default KonfiguratorSearchBar;
